<template>
  <list-page-shell>
    <template #actions>
      <el-button-group>
        <el-button size="small" @click="setPreset('week')">{{ $t('common.thisWeek') }}</el-button>
        <el-button size="small" @click="setPreset('month')">{{ $t('common.thisMonth') }}</el-button>
        <el-button size="small" @click="setPreset('year')">{{ $t('common.thisYear') }}</el-button>
      </el-button-group>
      <el-button size="small" @click="load">{{ $t('common.refresh') }}</el-button>
      <el-button size="small" @click="exportLead">{{ $t('reportPage.exportLead') }}</el-button>
      <el-button size="small" @click="exportProject">{{ $t('reportPage.exportProject') }}</el-button>
    </template>

    <template #filters>
      <el-form inline size="small">
        <el-form-item :label="$t('common.date')">
          <el-date-picker
            v-model="range"
            type="daterange"
            value-format="yyyy-MM-dd"
            :start-placeholder="$t('common.dateRangeStart')"
            :end-placeholder="$t('common.dateRangeEnd')"
            style="width: 320px;"
          />
        </el-form-item>
      </el-form>
    </template>

    <div class="report-metrics">
      <div class="metric-card">
        <span>{{ $t('nav.leads') }}</span>
        <strong>{{ overview.pipeline.leadCount || 0 }}</strong>
      </div>
      <div class="metric-card">
        <span>{{ $t('nav.quotes') }}</span>
        <strong>{{ formatUsd(overview.pipeline.quoteAmount) }}</strong>
      </div>
      <div class="metric-card">
        <span>{{ $t('nav.contracts') }}</span>
        <strong>{{ formatUsd(overview.pipeline.contractAmount) }}</strong>
      </div>
      <div class="metric-card">
        <span>{{ $t('projectPage.profit') }}</span>
        <strong>{{ formatUsd(overview.finance.profit) }}</strong>
      </div>
    </div>

    <el-row :gutter="16">
      <el-col :md="12" :xs="24">
        <el-card shadow="never">
          <div slot="header">{{ $t('reportPage.pipeline') }}</div>
          <div ref="chartLead" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :md="12" :xs="24">
        <el-card shadow="never">
          <div slot="header">{{ $t('reportPage.finance') }}</div>
          <div ref="chartPj" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" style="margin-top:16px;">
      <div slot="header">{{ $t('reportPage.projectFinanceSnapshot') }}</div>
      <el-table :data="projectRows" size="small" border>
        <el-table-column prop="projectCode" :label="$t('reportPage.tableProjectCode')" width="120" />
        <el-table-column prop="projectName" :label="$t('reportPage.tableProjectName')" min-width="180" />
        <el-table-column :label="$t('reportPage.tableRevenue')" width="120" align="right">
          <template slot-scope="scope">{{ formatUsd(scope.row.financeTotalContract != null ? scope.row.financeTotalContract : scope.row.revenueAmount) }}</template>
        </el-table-column>
        <el-table-column :label="$t('projectPage.totalReceived')" width="120" align="right">
          <template slot-scope="scope">{{ formatUsd(scope.row.financeReceived) }}</template>
        </el-table-column>
        <el-table-column :label="$t('reportPage.tableExpense')" width="120" align="right">
          <template slot-scope="scope">{{ formatUsd(scope.row.financeExpense) }}</template>
        </el-table-column>
        <el-table-column :label="$t('reportPage.tableProfit')" width="120" align="right">
          <template slot-scope="scope">{{ formatUsd(scope.row.financeProfit) }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </list-page-shell>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'
import ListPageShell from '@/components/ListPageShell.vue'
import { leadSummary, projectProfitReport, reportOverview } from '@/api/report'
import { labelLeadStatus } from '@/utils/crmLabels'
import { resolveApiBase } from '@/utils/apiBase'
import { formatUsd } from '@/utils/formatUsd'

const baseURL = resolveApiBase()

export default {
  name: 'Report',
  components: { ListPageShell },
  data() {
    return {
      range: [],
      overview: {
        pipeline: {},
        finance: {}
      },
      projectRows: []
    }
  },
  mounted() {
    this.load()
  },
  watch: {
    $locale() {
      this.load()
    }
  },
  methods: {
    formatUsd,
    setPreset(which) {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      if (which === 'week') {
        const day = now.getDay()
        const start = new Date(now)
        start.setDate(now.getDate() - day)
        const end = new Date(start)
        end.setDate(start.getDate() + 6)
        this.range = [fmt(start), fmt(end)]
      } else if (which === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        this.range = [fmt(start), fmt(end)]
      } else if (which === 'year') {
        const start = new Date(now.getFullYear(), 0, 1)
        const end = new Date(now.getFullYear(), 11, 31)
        this.range = [fmt(start), fmt(end)]
      }
      this.load()
    },
    params() {
      const p = {}
      if (this.range && this.range.length === 2) {
        p.startDate = this.range[0]
        p.endDate = this.range[1]
      }
      return p
    },
    async load() {
      const pr = this.params()
      const [a, b, c] = await Promise.all([
        leadSummary(pr),
        projectProfitReport(pr),
        reportOverview(pr)
      ])
      const ad = a.data || a
      const bd = b.data || b
      const cd = c.data || c
      this.overview = cd || { pipeline: {}, finance: {} }
      this.projectRows = bd.records || []

      const chart1 = echarts.init(this.$refs.chartLead)
      const byStatus = (ad && ad.byStatus) || {}
      const statusKeys = Object.keys(byStatus)
      chart1.setOption({
        title: { text: this.$t('reportPage.leadChartTitle'), textStyle: { color: '#cbd5e1' } },
        tooltip: {},
        xAxis: { type: 'category', data: statusKeys.map(k => labelLeadStatus(k)), axisLabel: { color: '#8b95a1' } },
        yAxis: { type: 'value', axisLabel: { color: '#8b95a1' } },
        series: [{ type: 'bar', data: statusKeys.map(k => byStatus[k]), itemStyle: { color: '#4ade80' } }]
      })

      const chart2 = echarts.init(this.$refs.chartPj)
      const revenue = bd && bd.sumRevenue ? Number(bd.sumRevenue) : 0
      const received = bd && bd.sumReceived ? Number(bd.sumReceived) : 0
      const expense = bd && bd.sumExpense ? Number(bd.sumExpense) : 0
      const profit = bd && bd.sumProfit ? Number(bd.sumProfit) : 0
      chart2.setOption({
        tooltip: {},
        legend: { textStyle: { color: '#8b95a1' } },
        series: [{
          type: 'pie',
          radius: ['45%', '70%'],
          label: { color: '#cbd5e1' },
          data: [
            { value: revenue, name: this.$t('reportPage.tableRevenue') },
            { value: received, name: this.$t('reportPage.financeReceived') },
            { value: expense, name: this.$t('reportPage.tableExpense') },
            { value: profit, name: this.$t('reportPage.tableProfit') }
          ]
        }]
      })
    },
    async exportLead() {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${baseURL}/api/report/lead-summary-export`, {
        params: this.params(),
        responseType: 'blob',
        headers: { Authorization: 'Bearer ' + token }
      })
      this.downloadBlob(res.data, 'lead-summary.xlsx')
    },
    async exportProject() {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${baseURL}/api/report/project-profit-export`, {
        params: this.params(),
        responseType: 'blob',
        headers: { Authorization: 'Bearer ' + token }
      })
      this.downloadBlob(res.data, 'project-profit.xlsx')
    },
    downloadBlob(blob, name) {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      a.click()
    }
  }
}
</script>

<style scoped>
.report-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.metric-card {
  padding: 16px 18px;
  border-radius: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-card);
}

.metric-card span {
  display: block;
  color: var(--color-text-sub);
  font-size: 12px;
  margin-bottom: 8px;
}

.metric-card strong {
  color: var(--color-text);
  font-size: 20px;
}

.chart-box {
  height: 320px;
}

@media (max-width: 900px) {
  .report-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
